import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Check, X, Crown, Star } from 'lucide-react'
import toast from 'react-hot-toast'
import { buildApiUrl } from '@/config/api'
import { loadStripe } from '@stripe/stripe-js'
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js'

interface SubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  feature: 'publish' | 'respond' | 'attachments'
  currentPlan?: 'FREE' | 'PRO'
  hasActiveSubscription?: boolean
}

interface Plan {
  id: string
  name: string
  price: number
  currency: string
  interval: string
  features: string[]
  limitations: string[]
}

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '')

export function SubscriptionModal({ isOpen, onClose, feature, currentPlan = 'FREE', hasActiveSubscription = false }: SubscriptionModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isDowngrading, setIsDowngrading] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)

  // Detect user's preferred currency based on location
  const [userCurrency, setUserCurrency] = useState<'USD' | 'EUR'>('USD')

  // Detect user's location on component mount
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Try to detect location using browser's timezone or IP geolocation
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const isEuropean = timezone.includes('Europe/') || 
                          timezone.includes('Berlin') || 
                          timezone.includes('Paris') || 
                          timezone.includes('Rome') || 
                          timezone.includes('Madrid') || 
                          timezone.includes('Amsterdam')
        
        setUserCurrency(isEuropean ? 'EUR' : 'USD')
      } catch (error) {
        console.log('Could not detect location, defaulting to USD')
        setUserCurrency('USD')
      }
    }
    
    detectCurrency()
  }, [])

  const getPlansForCurrency = (currency: 'USD' | 'EUR'): Plan[] => [
    {
      id: 'free',
      name: 'Free Plan',
      price: 0,
      currency,
      interval: 'month',
      features: [
        'Browse RFPs',
        'Add RFPs to watchlist',
        'View RFP details',
        'Basic search and filtering',
      ],
      limitations: [
        'Cannot publish RFPs',
        'Cannot respond to RFPs',
        'Limited access to advanced features',
      ],
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: 199,
      currency,
      interval: 'month',
      features: [
        'Everything in Free Plan',
        'Publish unlimited RFPs',
        'Respond to RFPs',
        'File attachments for RFPs',
        'Advanced analytics',
        'Priority support',
        'Export to PDF',
        'Direct messaging',
      ],
      limitations: [],
    },
  ]

  const plans = getPlansForCurrency(userCurrency)

  const handleUpgrade = async () => {
    setIsLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl('/api/subscription/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currency: userCurrency,
        }),
      })

      const data = await response.json()

      if (data.success && data.data.clientSecret) {
        setClientSecret(data.data.clientSecret)
        setShowCheckout(true)
      } else {
        toast.error(data.error || 'Failed to create checkout session')
      }
    } catch (error) {
      console.error('Error creating checkout session:', error)
      toast.error('Failed to create checkout session')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDowngrade = async () => {
    setIsDowngrading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Authentication required')
        return
      }

      const response = await fetch(buildApiUrl('/api/subscription/cancel'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Successfully downgraded to Free plan')
        onClose()
        // Refresh the page to update subscription status
        window.location.reload()
      } else {
        toast.error(data.error || 'Failed to downgrade subscription')
      }
    } catch (error) {
      console.error('Error downgrading subscription:', error)
      toast.error('Failed to downgrade subscription')
    } finally {
      setIsDowngrading(false)
    }
  }

  if (!isOpen) return null

  const featureText = feature === 'publish' 
    ? 'publish RFPs' 
    : feature === 'respond'
    ? 'respond to RFPs'
    : 'access file attachments'

  // Show embedded checkout if clientSecret is available
  if (showCheckout && clientSecret) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Crown className="h-8 w-8 text-yellow-500" />
                <div>
                  <h2 className="text-2xl font-bold">Complete Your Subscription</h2>
                  <p className="text-gray-600">
                    Secure payment powered by Stripe
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <Crown className="h-8 w-8 text-yellow-500" />
                <div>
                  <h2 className="text-2xl font-bold">
                    {currentPlan === 'PRO' 
                      ? 'Manage Subscription' 
                      : 'Upgrade to Pro'}
                  </h2>
                  <p className="text-gray-600">
                    {currentPlan === 'PRO'
                      ? 'You have a Pro plan. You can downgrade to Free if needed.'
                      : `Unlock the ability to ${featureText} and access premium features`}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Currency Switcher */}
                {currentPlan !== 'PRO' && (
                  <div className="flex items-center space-x-2 mr-4">
                    <span className="text-sm text-gray-600">Currency:</span>
                    <select 
                      value={userCurrency} 
                      onChange={(e) => setUserCurrency(e.target.value as 'USD' | 'EUR')}
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value="USD">🇺🇸 USD</option>
                      <option value="EUR">🇪🇺 EUR</option>
                    </select>
                  </div>
                )}
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {plans.map((plan) => (
              <Card key={plan.id} className={`relative ${plan.id === 'pro' ? 'border-2 border-blue-500' : ''}`}>
                {plan.id === 'pro' && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                      <Star className="h-3 w-3" />
                      <span>RECOMMENDED</span>
                    </div>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    {plan.id === 'pro' && <Crown className="h-5 w-5 text-yellow-500" />}
                    <span>{plan.name}</span>
                  </CardTitle>
                  <CardDescription>
                    <div className="text-3xl font-bold text-gray-900">
                      {plan.currency === 'EUR' ? '€' : '$'}{plan.price}
                      <span className="text-sm font-normal text-gray-500">/{plan.interval}</span>
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Features:</h4>
                      <ul className="space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center space-x-2 text-sm">
                            <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    {plan.limitations.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Limitations:</h4>
                        <ul className="space-y-2">
                          {plan.limitations.map((limitation, index) => (
                            <li key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                              <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                              <span>{limitation}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={onClose}>
              {hasActiveSubscription ? 'Close' : 'Maybe Later'}
            </Button>
            
            {currentPlan === 'PRO' ? (
              <Button 
                onClick={handleDowngrade} 
                disabled={isDowngrading}
                variant="outline"
                className="text-red-600 border-red-600 hover:bg-red-50"
              >
                {isDowngrading ? 'Downgrading...' : 'Downgrade to Free'}
              </Button>
            ) : (
              <Button 
                onClick={handleUpgrade} 
                disabled={isLoading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? 'Loading...' : `Upgrade to Pro - ${userCurrency === 'EUR' ? '€' : '$'}199/month`}
              </Button>
            )}
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Cancel anytime. No long-term commitment required.</p>

          </div>
        </div>
      </div>
    </div>
  )
}
